import { useEffect, useState } from 'react';
import NiceModal from '@ebay/nice-modal-react';
import { useParams } from 'react-router-dom';
import { Link } from 'react-router-dom';
import clsx from 'clsx';

import { DEFAULT_CURRENCY, Dinero } from 'itrvl-pricing';
import { useCampsInfoMapQuery } from 'queries/camps';
import { useExchangeRatesQuery } from 'queries';
import { useTransitSegments, useTransitAccommodations, useTransitPointsQuery, useTransitPointsSuggestedQuery } from 'queries/transit';
import {
  useItineraryQuery,
  useActivitiesBySupplierQuery,
  useActivitiesByLocationQuery,
  useActivitiesByCountryQuery,
  useConfirmNegativeMargin,
} from 'api';
import ConfirmDenyModal from 'components/v2/Modals/ConfirmDenyModal';
import Button from 'components/v2/Button';

import logger from 'itrvl-logger';
const log = logger(__filename);

export const useItinerary = () => {
  const { id } = useParams();
  const query = useItineraryQuery(id);
  return {
    id,
    instance: query?.data,
    ...query,
  };
};

export const useSegmentHasDiscount = segment => {
  const discountAmount = Number.parseFloat(segment?.discountAmount || 0);
  const hasDiscount = discountAmount !== 0;
  return { discountAmount, hasDiscount };
};

export const useAmountInTripCurrency = () => {
  const { instance } = useItinerary();
  const currency = instance?.currency ?? DEFAULT_CURRENCY;
  const { data: exchange } = useExchangeRatesQuery(instance?.finance?.currency ?? DEFAULT_CURRENCY);
  function getAmount(amount = 0, segmentCurrency = DEFAULT_CURRENCY) {
    try {
      return Dinero.exchangeSync(Dinero({ amount, currency: segmentCurrency }), currency, {
        [currency]: exchange[currency],
      });
    } catch {
      log.info(`currency conversion failed for: ${currency}, falling back. amount: ${amount}`);
      return Dinero({ amount, currency: segmentCurrency });
    }
  }
  return {
    tripCurrency: currency,
    getAmount,
  };
};

// @todo: this seems like it's doing a lot of extra work, can probably be cleaned up
export const useSegmentCost = (segment, instance) => {
  if (!segment) {
    return log.warn('useSegmentCost: segment is not defined');
  }
  if (segment.arrangedBy === 'DMC') {
    return 'Included';
  }

  const currency = instance.currency;
  const segmentCostInTripCurrency = Dinero.exchangeSync(
    Dinero({ amount: segment?.costAmount ?? 0, currency: segment?.currency ?? DEFAULT_CURRENCY }),
    currency,
    {
      [currency]: instance.spotRates,
    },
  );

  const amount = segmentCostInTripCurrency.getAmount() || 0;
  const segmentCost = Dinero({ amount, currency: instance.currency });
  return {
    segmentCostFormatted: segmentCost.toFormat(),
    segmentCost,
    segmentCostInTripCurrency,
    amount,
  };
};

export const useSegmentSell = (segment, currency, itinerary) => {
  if (!segment) {
    log.warn('useSegmentSell: segment is not defined');
  }
  if (segment.arrangedBy === 'DMC') {
    const dmc = itinerary.itinerary.segments.find(s => segment.linked === s.sequence) ?? {};
    if (!dmc.supplierName) {
      return (
        <Button
          variant="contained"
          variantColor="light"
          disableElevation
          component={Link}
          to={`/itinerary/${itinerary.id}/edit-dmc`}
          className={clsx('edit-btn')}
        >
          Edit DMC
        </Button>
      );
    }
    return `${dmc.supplierName} (${dmc.sequence}) - ${dmc.currency}`;
  }
  return Dinero({ unit: segment?.sell || 0, currency }).toFormat();
};

export const useCampInfo = (supplierCode, instance) => {
  const query = useCampsInfoMapQuery({ enabled: Boolean(instance?.id), instance, supplierCode });
  let campInfo = {};
  if (query.isFetched) {
    campInfo = query?.data?.[supplierCode];
  }
  return {
    ...query,
    campInfo,
  };
};

export const useTransitOrig = ({ segment, segments, index, fromOverride, toOverride }) => {
  const query = useTransitSegments({ segment, segments, index, fromOverride, toOverride });
  return {
    ...query,
    results: query.isFetched ? query.data : [],
  };
};

export const useTransit = ({ segment, segments, index }) => {
  const query = useTransitAccommodations({ segment, segments, index });
  return {
    ...query,
    results: query.isFetched ? query.data : {},
  };
};

export const useTransitPoints = () => {
  const query = useTransitPointsQuery();
  return {
    ...query,
    results: query.isFetched ? query.data : {},
  };
};

export const useTransitPointsSuggested = supplierCodes => {
  const query = useTransitPointsSuggestedQuery({ supplierCodes });
  return {
    ...query,
    results: query.isFetched ? query.data : {},
  };
};

export const useActivitiesBySupplier = ({ segment, segments, index }) => {
  const query = useActivitiesBySupplierQuery({ segment, segments, index });
  return {
    ...query,
    results: query.isFetched ? query.data : {},
  };
};

export const useActivitiesByLocation = ({ segment, segments, index, options }) => {
  const query = useActivitiesByLocationQuery({ segment, segments, index, options });
  return {
    ...query,
    results: query.isFetched ? query.data : {},
  };
};

export const useActivitiesByCountry = ({ segment, segments, index, options }) => {
  const query = useActivitiesByCountryQuery({ segment, segments, index, options });
  return {
    ...query,
    results: query.isFetched ? query.data : {},
  };
};

export const useNegativeMarginCheck = itinerary => {
  const { mutate: confirmNegativeMarginMutate } = useConfirmNegativeMargin();
  const [ignore, setIgnore] = useState(false);
  useEffect(() => {
    if (itinerary.hasMarginProblem && !itinerary.hasSuppressMarginFlag && !ignore && itinerary.hasPayments) {
      NiceModal.show(ConfirmDenyModal, {
        fullWidth: true,
        maxWidth: 'sm',
        title: 'Negative Margin',
        subtext: 'This itinerary has a negative margin. Payments cannot be made for this itinerary until this is resolved.',
        deniedProps: {
          text: 'Ignore For Now',
        },
        confirmedProps: {
          text: 'Suppress Error',
        },
        onCancel: modal => {
          modal.hide();
          setIgnore(true);
        },
        onConfirm: modal => {
          confirmNegativeMarginMutate({
            itinerary,
            onSuccess: modal.hide,
            onError: error => modal.show({ ...modal.args, loading: false, error }),
          });
          modal.show({ ...modal.args, loading: true });
        },
      });
    }
  }, [itinerary, confirmNegativeMarginMutate, ignore]);
};

export const useSetDocumentTitle = (pageName, itinerary) => {
  useEffect(() => {
    if (itinerary?.name) {
      document.title = `${pageName} - ${itinerary.name}`;
    }
    return () => {
      document.title = `${pageName}`;
    };
  }, [pageName, itinerary]);
};
